// Node-based post-processing for WebGPURenderer
// Stack: scene → bloom → chromatic aberration → film grain → vignette

import { PostProcessing } from 'three/webgpu';
import type { WebGPURenderer, PerspectiveCamera, Scene } from 'three/webgpu';
import {
  pass, screenUV, screenCoordinate, screenSize,
  float, vec2, vec3, smoothstep, sub, fract,
  hash, time,
} from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import type { RenderCapabilities } from './detect-capabilities';
import { QUALITY_BUDGETS } from './detect-capabilities';

export interface PostProcessingContext {
  postProcessing: PostProcessing;
  dispose: () => void;
}

export function createPostProcessing(
  renderer: WebGPURenderer,
  scene: Scene,
  camera: PerspectiveCamera,
  caps: RenderCapabilities,
): PostProcessingContext {
  const budget = QUALITY_BUDGETS[caps.qualityLevel];
  const postProcessing = new PostProcessing(renderer);

  const scenePass = pass(scene, camera);
  const scenePassColor = scenePass.getTextureNode('output');

  // --- Chromatic aberration ---
  // Shift R and B channels in opposite directions from screen center
  const center = vec2(0.5, 0.5);
  const dirFromCenter = screenUV.sub(center);
  const radialDist = dirFromCenter.length();
  const pixelOffset = float(1.5);
  const uvOffset = dirFromCenter.normalize().mul(pixelOffset).div(screenSize);
  const aberrationScale = smoothstep(0.0, float(0.7), radialDist);

  const uvR = screenUV.add(uvOffset.mul(aberrationScale));
  const uvB = screenUV.sub(uvOffset.mul(aberrationScale));

  // Apply aberration to scene texture, then add bloom on top
  let outputNode = vec3(
    scenePassColor.sample(uvR).r,
    scenePassColor.g,
    scenePassColor.sample(uvB).b,
  );

  // Bloom — skip entirely when disabled by quality budget
  if (budget.bloomEnabled) {
    outputNode = outputNode.add(bloom(scenePassColor, budget.bloomThreshold, budget.bloomStrength, budget.bloomRadius));
  }

  // --- Film grain ---
  const grainSeed = fract(
    screenCoordinate.x.add(screenCoordinate.y.mul(314.159)).add(time.mul(43758.5453)),
  );
  const grain = hash(grainSeed).sub(0.5).mul(float(0.04));
  outputNode = outputNode.add(grain);

  // --- Vignette ---
  if (budget.vignetteEnabled) {
    const dist = screenUV.sub(center).length();
    const strength = float(0.3);
    const vignette = sub(1.0, smoothstep(0.0, float(0.8), dist).mul(strength));
    outputNode = outputNode.mul(vignette);
  }

  postProcessing.outputNode = outputNode;

  function dispose(): void {
    postProcessing.dispose();
  }

  return { postProcessing, dispose };
}
